import { FilterPopover } from 'restaurantAdmin/components/dateAndGuestCountFilterPill/FilterPopover';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useAvailabilityDrawerContext } from 'restaurantAdmin/reservations/concierge/state/AvailabilityDrawerContext';
import { GodModeOverbookingWarningModal } from 'restaurantAdmin/reservations/shared/GodModeOverbookingWarningModal';
import { FloorPlanSelector } from '@components/floorPlan/FloorPlanSelector';
import { StripeElements } from '@components/stripe/StripeElements';
import { TZDate } from '@date-fns/tz';
import { DateAndGuestCountFilterPill } from '@shared/availability/DateAndGuestCountFilterPill';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { useIsOpenWithData } from '@shared/hooks/useIsOpenWithData';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import { useSearchParams } from 'react-router-dom';
import { PageContent } from '../../layout/PageContent';
import { PageHeader } from '../../layout/PageHeader';
import {
  checkMultiTableReservationForOverlaps,
  type OverlappingReservation,
} from './apiHelpers';
import { ConciergeAdminAvailability } from './ConciergeAdminAvailability';
import { useConciergeAvailabilityContext } from './ConciergeAvailabilityContext';
import { ConciergeDrawer } from './ConciergeDrawer';
import styles from './ConciergePage.scss';
import { ConciergePageNavLinks } from './ConciergePageNavLinks';
import { InteractiveConciergeFloorPlan } from './InteractiveConciergeFloorPlan';
import {
  MultiTableBookingForm,
  type MultiTableReservationFormData,
} from './MultiTableBookingForm';
import { ReserveButton } from './ReserveButton';

export const ConciergePage = () => {
  const [_searchParams, setSearchParams] = useSearchParams();
  const { isOpen: isAvailabilityDrawerOpen, openDrawer } =
    useAvailabilityDrawerContext();
  const { timezone, id: restaurantId } = useRestaurant();
  const {
    clearSelectedAvailability,
    isLoading,
    selectedAvailability,
    selectedDate,
    selectedGuestCount,
    setSelectedDate,
    setSelectedGuestCount,
    selectedFloorPlan,
    floorPlans,
    setSelectedFloorPlanId,
    selectedFloorPlanTableIds,
    selectedFloorPlanTables,
    multiTableMode,
  } = useConciergeAvailabilityContext();
  const {
    close: closeGuestCountAndDateFilter,
    isOpen: isGuestCountAndDateFilterOpen,
    open: openGuestCountAndDateFilter,
  } = useIsOpen();

  const handleReserveClick = () => {
    if (selectedAvailability) {
      openDrawer({
        date: selectedAvailability.date,
        guestCount: selectedGuestCount,
        listingPrice: selectedAvailability.purchasePrice,
        listingId: selectedAvailability.listing.id,
        time: selectedAvailability.time,
        isBestAvailable: selectedAvailability.isBestAvailable,
      });
    }
  };

  const openMultiTableBookReservationDrawer = ({
    date,
    time,
    guestCount,
    duration,
  }: MultiTableReservationFormData) =>
    openDrawer({
      date,
      time,
      guestCount,
      duration,
      tableIds: selectedFloorPlanTableIds,
      listingPrice: 0, // Reservation price doesn't come from the listing, admins must set a custom price.
      multiTable: true,
    });

  const handleFilterApply = (appliedDate: Date, appliedGuestCount: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('date', toISODateFormat(appliedDate));
      return newParams;
    });
    setSelectedGuestCount(appliedGuestCount);
    closeGuestCountAndDateFilter();
    clearSelectedAvailability();
  };

  const {
    isOpen: isUnassignmentWarningModalOpen,
    open: openUnassignmentWarningModal,
    close: closeUnassignmentWarningModal,
    data: modalData,
  } = useIsOpenWithData<{
    formData: MultiTableReservationFormData;
    overlappingReservations: OverlappingReservation[];
  }>();

  const handleReserveClickMultiTable = async (
    formData: MultiTableReservationFormData,
  ) => {
    const { wouldOverlap, overlappingReservations } =
      await checkMultiTableReservationForOverlaps(restaurantId, {
        date: formData.date,
        duration: Number(formData.duration),
        time: formData.time,
        floorPlanTableIds: selectedFloorPlanTableIds,
      });
    if (wouldOverlap) {
      openUnassignmentWarningModal({ formData, overlappingReservations });
    } else {
      openMultiTableBookReservationDrawer(formData);
    }
  };

  const renderContent = () => {
    if (isLoading) return null;
    return (
      <>
        <div className={styles.content}>
          {multiTableMode ? (
            <>
              <div className={styles.sidePanel}>
                <MultiTableBookingForm
                  tableLabelPosition="bottom"
                  submitButtonText="Book Tables"
                  handleSubmitClick={handleReserveClickMultiTable}
                  selectedFloorPlanTables={selectedFloorPlanTables}
                />
              </div>
              {modalData && (
                <GodModeOverbookingWarningModal
                  isOpen={isUnassignmentWarningModalOpen}
                  onClose={closeUnassignmentWarningModal}
                  onConfirm={openMultiTableBookReservationDrawer}
                  formData={modalData.formData}
                  overlappingReservations={modalData.overlappingReservations}
                />
              )}
            </>
          ) : (
            <SingleTableBookingSidePanel
              selectedDate={selectedDate}
              selectedGuestCount={selectedGuestCount}
              timezone={timezone}
              closeGuestCountAndDateFilter={closeGuestCountAndDateFilter}
              isGuestCountAndDateFilterOpen={isGuestCountAndDateFilterOpen}
              openGuestCountAndDateFilter={openGuestCountAndDateFilter}
              handleFilterApply={handleFilterApply}
              handleReserveClick={handleReserveClick}
            />
          )}

          <div className={styles.floorPlanContainer}>
            <FloorPlanSelector
              floorPlans={floorPlans}
              selectedFloorPlan={selectedFloorPlan}
              onChange={(selectedFloorPlanId) => {
                setSelectedFloorPlanId(selectedFloorPlanId);
              }}
            />

            <InteractiveConciergeFloorPlan
              backgroundSrc={selectedFloorPlan.backgroundSrc}
              tables={selectedFloorPlan.floorPlanTables}
              multiSelect={multiTableMode}
            />
          </div>
        </div>
        {isAvailabilityDrawerOpen && <ConciergeDrawer />}
      </>
    );
  };

  return (
    <StripeElements>
      <PageHeader category="Reservations" title="Concierge">
        <ConciergePageNavLinks />
      </PageHeader>
      <PageContent className={styles.pageContent}>
        {renderContent()}
      </PageContent>
    </StripeElements>
  );
};

type SingleTableBookingSidePanelProps = {
  selectedDate: Date;
  selectedGuestCount: number;
  timezone: string;
  isGuestCountAndDateFilterOpen: boolean;
  closeGuestCountAndDateFilter: () => void;
  openGuestCountAndDateFilter: () => void;
  handleFilterApply: (date: Date, guestCount: number) => void;
  handleReserveClick: () => void;
};

const SingleTableBookingSidePanel = ({
  selectedDate,
  selectedGuestCount,
  timezone,
  closeGuestCountAndDateFilter,
  isGuestCountAndDateFilterOpen,
  openGuestCountAndDateFilter,
  handleFilterApply,
  handleReserveClick,
}: SingleTableBookingSidePanelProps) => {
  return (
    <div className={styles.sidePanel}>
      <DateAndGuestCountFilterPill
        closeFilter={closeGuestCountAndDateFilter}
        isOpenFilter={isGuestCountAndDateFilterOpen}
        openFilter={openGuestCountAndDateFilter}
        selectedDate={selectedDate}
        selectedGuestCount={selectedGuestCount}
        popover={
          <FilterPopover
            onApply={handleFilterApply}
            selectedDate={selectedDate}
            selectedGuestCount={selectedGuestCount}
            minimumDate={TZDate.tz(timezone)}
          />
        }
      />
      <ConciergeAdminAvailability
        availabilityItemAction={
          <ReserveButton handleClick={handleReserveClick} />
        }
      />
    </div>
  );
};
